import type { UploadModel } from 'interfaces/app';
import type { SnapshotFrom } from 'xstate';
import {
	getUploadItemProgress,
	type UploadBatchItemSnapshot,
	type UploadBatchItemStatus,
	type UploadItemContext,
	uploadItemMachine,
} from './machines';

export type UploadQueueBucket = 'waiting' | 'uploading' | 'error' | 'completed' | 'none';

export type UploadModelProjection = {
	bucket: UploadQueueBucket;
	model: UploadModel;
	saveBlocking: boolean;
	retryable: boolean;
};

export type UploadItemSnapshot = SnapshotFrom<typeof uploadItemMachine>;

/*
 * Single source of truth for the projected photo-model id. The session's
 * buildActiveModel passes the metadata-provided id as the middle fallback;
 * standalone item projections have no metadata and skip it.
 */
export function resolveUploadPhotoModelId(
	context: Pick<UploadItemContext, 'remotePhotoId' | 'localPhotoId'>,
	metadataPhotoModelId?: string | number | null,
): string | number | null {
	return context.remotePhotoId ?? metadataPhotoModelId ?? context.localPhotoId;
}

export function projectUploadItemSnapshot(snapshot: UploadItemSnapshot): UploadModelProjection {
	const { context } = snapshot;
	const queue = context.totalBytes > 0
		? context.uploadedBytes / context.totalBytes
		: 0;
	const photoModelId = resolveUploadPhotoModelId(context);
	const inFailedState = (
		snapshot.matches('uploadFailed')
		|| snapshot.matches('processingFailed')
		|| snapshot.matches('rejected')
	);

	return {
		bucket: getUploadQueueBucket(snapshot),
		model: {
			id: context.uploadId,
			name: context.name,
			totalBytes: context.totalBytes,
			queue,
			overall: getUploadItemProgress(context),
			/*
			 * errorCount is "retries performed, plus one while the item is
			 * currently sitting in a failed state". A retried-then-saved item
			 * therefore reports its retry count, not a failure count.
			 */
			errorCount: context.retryCount + (inFailedState ? 1 : 0),
			photoModelId,
			uploadEnd: null,
			uploadStart: null,
			url: context.uploadUrl ?? undefined,
		},
		saveBlocking: snapshot.hasTag('save-blocking'),
		retryable: snapshot.can({
			type: 'upload.retry',
		}),
	};
}

export function projectUploadBatchItemSnapshot(snapshot: UploadItemSnapshot): UploadBatchItemSnapshot {
	const { context } = snapshot;

	return {
		id: context.uploadId,
		status: getUploadBatchItemStatus(snapshot),
		uploadedBytes: context.uploadedBytes,
		totalBytes: context.totalBytes,
		saveBlocking: snapshot.hasTag('save-blocking'),
	};
}

function getUploadQueueBucket(snapshot: UploadItemSnapshot): UploadQueueBucket {
	if (
		snapshot.matches('selected')
		|| snapshot.matches('validating')
		|| snapshot.matches('creatingLocalPhoto')
		|| snapshot.matches('queuedForUpload')
	) {
		return 'waiting';
	}

	if (
		snapshot.matches('uploading')
		|| snapshot.matches('uploaded')
		|| snapshot.matches('processingPhoto')
	) {
		return 'uploading';
	}

	if (snapshot.matches('uploadFailed') || snapshot.matches('processingFailed')) {
		return 'error';
	}

	if (snapshot.matches('saved')) {
		return 'completed';
	}

	/*
	 * Terminal non-saved items belong to no bucket: their bookkeeping moved
	 * to the session (completedItems / batch removal) at termination.
	 */
	if (
		snapshot.matches('rejected')
		|| snapshot.matches('cancelled')
		|| snapshot.matches('removed')
	) {
		return 'none';
	}

	throw new Error(`Unmapped upload item state: ${JSON.stringify(snapshot.value)}`);
}

function getUploadBatchItemStatus(snapshot: UploadItemSnapshot): UploadBatchItemStatus {
	if (
		snapshot.matches('selected')
		|| snapshot.matches('queuedForUpload')
	) {
		return 'pending';
	}

	if (
		snapshot.matches('validating')
		|| snapshot.matches('creatingLocalPhoto')
		|| snapshot.matches('uploading')
		|| snapshot.matches('uploaded')
		|| snapshot.matches('processingPhoto')
	) {
		return 'active';
	}

	if (snapshot.matches('uploadFailed') || snapshot.matches('processingFailed')) {
		return 'failed';
	}

	if (snapshot.matches('saved')) {
		return 'saved';
	}

	if (snapshot.matches('rejected')) {
		return 'rejected';
	}

	if (snapshot.matches('cancelled')) {
		return 'cancelled';
	}

	if (snapshot.matches('removed')) {
		return 'removed';
	}

	throw new Error(`Unmapped upload item state: ${JSON.stringify(snapshot.value)}`);
}
